import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  createMessageHistoryApi,
  MessageHistoryItem,
  MessageHistoryResponse,
} from "../api/messageHistoryApi";
import { useAuthenticatedApi } from "./useAuthenticatedApi";
import { usePagination } from "./usePagination";

interface UseMessageHistoryOptions {
  hotelId: string;
  initialPage?: number;
  initialLimit?: number;
  initialType?: string;
  initialSearch?: string;
  initialSenderPlatformUserId?: string;
  initialReceiverPlatformUserId?: string;
  initialRequestId?: string;
}

export const useMessageHistory = ({
  hotelId,
  initialPage = 1,
  initialLimit = 20,
  initialType = "",
  initialSearch = "",
  initialSenderPlatformUserId = "",
  initialReceiverPlatformUserId = "",
  initialRequestId = "",
}: UseMessageHistoryOptions) => {
  const { callApi } = useAuthenticatedApi();
  const { pagination, setPage, setLimit, setTotal } = usePagination({
    initialPage,
    initialLimit,
  });

  const [type, setType] = useState(initialType);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [senderPlatformUserId, setSenderPlatformUserId] = useState(
    initialSenderPlatformUserId
  );
  const [receiverPlatformUserId, setReceiverPlatformUserId] = useState(
    initialReceiverPlatformUserId
  );
  const [requestId, setRequestId] = useState(initialRequestId);

  const api = useMemo(() => createMessageHistoryApi(callApi), [callApi]);

  const normalizedType = useMemo(() => type.trim(), [type]);
  const normalizedSearch = useMemo(() => searchTerm.trim(), [searchTerm]);
  const normalizedSenderId = useMemo(
    () => senderPlatformUserId.trim(),
    [senderPlatformUserId]
  );
  const normalizedReceiverId = useMemo(
    () => receiverPlatformUserId.trim(),
    [receiverPlatformUserId]
  );
  const normalizedRequestId = useMemo(() => requestId.trim(), [requestId]);
  const isIdLikeSearch = useMemo(() => {
    if (!normalizedSearch) return false;
    return normalizedSearch.includes("-");
  }, [normalizedSearch]);

  const queryKey = useMemo(
    () => [
      "messageHistory",
      hotelId,
      pagination.page,
      pagination.limit,
      normalizedType,
      normalizedSearch,
      isIdLikeSearch,
      normalizedSenderId,
      normalizedReceiverId,
      normalizedRequestId,
    ],
    [
      hotelId,
      pagination.page,
      pagination.limit,
      normalizedType,
      normalizedSearch,
      isIdLikeSearch,
      normalizedSenderId,
      normalizedReceiverId,
      normalizedRequestId,
    ]
  );

  const { data, isLoading, error, refetch } = useQuery<
    MessageHistoryResponse,
    Error
  >({
    queryKey,
    enabled: Boolean(hotelId),
    queryFn: async () => {
      return api.fetchMessages({
        page: pagination.page,
        limit: pagination.limit,
        type: normalizedType || undefined,
        // For UUID-like searches, avoid overly restrictive server-side search
        // and rely on client-side ID matching fallback for the current page.
        search:
          normalizedSearch && !isIdLikeSearch ? normalizedSearch : undefined,
        senderPlatformUserId: normalizedSenderId || undefined,
        receiverPlatformUserId: normalizedReceiverId || undefined,
        requestId: normalizedRequestId || undefined,
      });
    },
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (data?.meta?.totalCount !== undefined && data.meta.totalCount !== pagination.total) {
      setTotal(data.meta.totalCount);
    }
  }, [data?.meta?.totalCount, pagination.total, setTotal]);

  useEffect(() => {
    setPage(1);
  }, [
    normalizedType,
    normalizedSearch,
    normalizedSenderId,
    normalizedReceiverId,
    normalizedRequestId,
    setPage,
  ]);

  const messages = useMemo(() => {
    const source = data?.data ?? [];

    // Backend currently guarantees type/search filtering. We still apply
    // sender/receiver/request filters on the fetched page for compatibility
    // with endpoints that may not support those query params yet.
    return source.filter((message: MessageHistoryItem) => {
      if (normalizedSearch && isIdLikeSearch) {
        const searchValue = normalizedSearch.toLowerCase();
        const senderId = (message.sender?.platformUserId || "").toLowerCase();
        const receiverId = (message.receiver?.platformUserId || "").toLowerCase();
        const msgRequestId = (message.requestId || "").toLowerCase();
        const msgId = (message.messageId || "").toLowerCase();

        const matchesIdSearch =
          senderId.includes(searchValue) ||
          receiverId.includes(searchValue) ||
          msgRequestId.includes(searchValue) ||
          msgId.includes(searchValue);

        if (!matchesIdSearch) {
          return false;
        }
      }

      if (
        normalizedSenderId &&
        (message.sender?.platformUserId || "") !== normalizedSenderId
      ) {
        return false;
      }

      if (
        normalizedReceiverId &&
        (message.receiver?.platformUserId || "") !== normalizedReceiverId
      ) {
        return false;
      }

      if (normalizedRequestId && (message.requestId || "") !== normalizedRequestId) {
        return false;
      }

      return true;
    });
  }, [
    data?.data,
    normalizedSearch,
    isIdLikeSearch,
    normalizedSenderId,
    normalizedReceiverId,
    normalizedRequestId,
  ]);

  return {
    messages,
    rawMessages: data?.data ?? [],
    isLoading,
    error: error ?? null,
    pagination,
    setPage,
    setLimit,
    refetch,
    filters: {
      type,
      searchTerm,
      senderPlatformUserId,
      receiverPlatformUserId,
      requestId,
    },
    setType,
    setSearchTerm,
    setSenderPlatformUserId,
    setReceiverPlatformUserId,
    setRequestId,
  };
};
